import { useCallback, useMemo } from 'react';
import { useDialog } from '@gokrew/ui';
import type { CrmDealStage } from '@/api/crm';
import { useCrmFormat } from './crm-format';
import { CrmDealEditor } from './CrmDealEditor';
import { CrmDealDetail } from './CrmDealDetail';
import { CrmCompanyEditor } from './CrmCompanyEditor';
import { CrmContactEditor } from './CrmContactEditor';
import { CrmActivityEditor } from './CrmActivityEditor';
import styles from './Crm.module.css';

/** The CRM's create/open dialogs, shared by pages and the Cmd+K palette. */
export const useCrmDialogs = () => {
  const { t } = useCrmFormat();
  const { openDialog } = useDialog();
  const showDeal = useCallback(
    (id: string) =>
      openDialog({
        title: t('crm.workspace.opportunity'),
        containerClass: styles.detailDialog,
        content: <CrmDealDetail id={id} />,
      }),
    [openDialog, t]
  );
  const newDeal = useCallback(
    (stage?: CrmDealStage) =>
      openDialog({
        title: t('crm.workspace.new_deal'),
        containerClass: styles.editorDialog,
        content: (
          <CrmDealEditor
            initialStage={stage}
            onSaved={(deal) => showDeal(deal.id)}
          />
        ),
      }),
    [openDialog, t, showDeal]
  );
  const newCompany = useCallback(
    () =>
      openDialog({
        title: t('crm.add_company'),
        containerClass: styles.editorDialog,
        content: <CrmCompanyEditor />,
      }),
    [openDialog, t]
  );
  const newContact = useCallback(
    () =>
      openDialog({
        title: t('crm.add_contact'),
        containerClass: styles.editorDialog,
        content: <CrmContactEditor />,
      }),
    [openDialog, t]
  );
  const newActivity = useCallback(
    () =>
      openDialog({
        title: t('crm.workspace.add_activity'),
        containerClass: styles.editorDialog,
        content: <CrmActivityEditor />,
      }),
    [openDialog, t]
  );
  return useMemo(
    () => ({ showDeal, newDeal, newCompany, newContact, newActivity }),
    [showDeal, newDeal, newCompany, newContact, newActivity]
  );
};
